"use client";

import { useState } from "react";
import { Search, Plus } from "lucide-react";
import { TreatmentsPanel, Treatment } from "../../../components/treatments/TreatmentsPanel";
import { TreatmentModal, TreatmentFormData } from "../../../components/treatments/TreatmentModal";
import PageHeader from "../../../components/ui/PageHeader";
import { Briefcase } from "lucide-react";

// Demo data
const DEMO_TREATMENTS: Treatment[] = [
  {
    id: "1",
    name: "Botox - Forehead Lines",
    price: 250,
    duration: 30,
    status: "active",
    description: "Anti-wrinkle treatment for forehead lines",
  },
  {
    id: "2",
    name: "Dermal Fillers - Lips",
    price: 350,
    duration: 45,
    status: "active",
    description: "Lip enhancement and definition",
  },
  {
    id: "3",
    name: "Chemical Peel",
    price: 150,
    duration: 60,
    status: "active",
    description: "Skin rejuvenation treatment",
  },
  {
    id: "4",
    name: "Microneedling",
    price: 200,
    duration: 45,
    status: "inactive",
    description: "Collagen induction therapy",
  },
  {
    id: "5",
    name: "Laser Hair Removal - Full Legs",
    price: 180,
    duration: 60,
    status: "active",
    description: "Permanent hair reduction treatment",
  },
];

export default function ManageTreatments() {
  const [searchTerm, setSearchTerm] = useState("");
  const [treatments, setTreatments] = useState<Treatment[]>(DEMO_TREATMENTS);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingTreatment, setEditingTreatment] = useState<Treatment | null>(null);

  // Filter treatments by search
  const filteredTreatments = treatments.filter((t) =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  // Handlers
  const handleAddTreatment = () => {
    setModalMode("add");
    setEditingTreatment(null);
    setShowModal(true);
  };

  const handleEditTreatment = (treatment: Treatment) => {
    setModalMode("edit");
    setEditingTreatment(treatment);
    setShowModal(true);
  };

  const handleDeleteTreatment = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      setTreatments((prev) => prev.filter((t) => t.id !== id));
    }
  };

  const handleSaveTreatment = (data: TreatmentFormData) => {
    if (modalMode === "add") {
      const newTreatment: Treatment = {
        id: Date.now().toString(),
        name: data.name,
        price: data.price,
        duration: data.duration,
        status: data.status,
        description: data.description,
      };
      setTreatments((prev) => [...prev, newTreatment]);
    } else if (editingTreatment) {
      setTreatments((prev) =>
        prev.map((t) =>
          t.id === editingTreatment.id
            ? { ...t, ...data }
            : t
        )
      );
    }
    setShowModal(false);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col w-full h-full">

        {/* Header */}
        <PageHeader
          title="Treatments"
          description="Manage your aesthetic treatments and offerings"
          icon={Briefcase}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={true}
          actions={
            <button
              onClick={handleAddTreatment}
              className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
              <span>Add Treatment</span>
            </button>
          }
        />

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
          {/* Left: Count */}
          <div className="text-sm text-gray-500 font-medium">
            {filteredTreatments.length} treatment{filteredTreatments.length !== 1 ? "s" : ""}
          </div>

          {/* Right: Search */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search treatments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-6">
          <TreatmentsPanel
            treatments={filteredTreatments}
            onTreatmentEdit={handleEditTreatment}
            onTreatmentDelete={handleDeleteTreatment}
          />
        </div>

        {/* Modal */}
        <TreatmentModal
          isOpen={showModal}
          mode={modalMode}
          treatment={editingTreatment}
          onClose={() => setShowModal(false)}
          onSave={handleSaveTreatment}
        />
      </div>
    </div>
  );
}
